import React from 'react';
import { getContentTypeIcon, getContentTypeName } from '../../utils/designTokens';
import { badge } from '../../utils/designTokens';

/**
 * TypeBadge - Display a content type badge with icon and label
 * Used to indicate whether content is a Prompt, AI Agent, or MCP Server
 *
 * @param {string} type - Content type: 'prompt', 'agent', or 'mcp_server'
 * @param {string} size - Size variant: 'sm', 'md', 'lg' (default: 'md')
 * @param {boolean} showIcon - Whether to show the icon (default: true)
 * @param {boolean} showLabel - Whether to show the label text (default: true)
 * @param {string} className - Additional CSS classes
 */
const TypeBadge = ({
  type = 'prompt',
  size = 'md',
  showIcon = true,
  showLabel = true,
  className = ''
}) => {
  // Get the badge style based on content type
  const badgeClass = badge[type] || badge.prompt;

  // Size variants
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-1.5 text-base'
  };

  const iconSizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  };

  // Override size in badge class with our size variant
  const finalBadgeClass = badgeClass
    .replace('px-3 py-1 text-sm', sizeClasses[size]);

  return (
    <span
      className={`inline-flex items-center gap-1.5 ${finalBadgeClass} ${className}`}
      role="status"
      aria-label={`Content type: ${getContentTypeName(type)}`}
    >
      {showIcon && (
        <span className={iconSizes[size]} aria-hidden="true">
          {getContentTypeIcon(type)}
        </span>
      )}
      {showLabel && (
        <span className="font-semibold">
          {getContentTypeName(type)}
        </span>
      )}
    </span>
  );
};

export default TypeBadge;
